<template>
  <!-- [sc-49299] Nuovo articolo dal form del preventivo: categoria, poi i
       campi. Va in catalogo e in magazzino, poi torna al preventivo -->
  <VueFinalModal
    :model-value="show"
    @update:model-value="$emit('update:show', $event)"
    :click-to-close="false"
    :esc-to-close="true"
    classes="documents-modal"
    content-class="crea-articolo-modal-content"
  >
    <template #default>
      <button
        class="modal__close icons-button"
        @click="chiudi"
        title="Chiudi"
        type="button"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="modal__title">Nuovo articolo</h3>

      <!-- Passo 1: categoria -->
      <div v-show="!categoria" class="crea-articolo-passo">
        <p class="crea-articolo-sottotitolo">Scegli la categoria</p>
        <div class="crea-articolo-categorie">
          <button
            v-for="cat in categorie"
            :key="cat.slug"
            type="button"
            class="crea-articolo-categoria"
            @click="scegliCategoria(cat.slug)"
          >
            <i :class="cat.icon" aria-hidden="true"></i>
            <span>{{ cat.label }}</span>
          </button>
        </div>
      </div>

      <!-- Passo 2: campi. Resta montato anche tornando alla categoria, così i
           campi comuni non si perdono -->
      <form
        v-if="categoriaUsata"
        v-show="categoria"
        class="crea-articolo-passo"
        @submit.prevent="salva"
      >
        <p class="crea-articolo-sottotitolo">
          Categoria: <strong>{{ etichettaCategoria }}</strong>
          <button
            type="button"
            class="crea-articolo-cambia"
            @click="categoria = ''"
          >
            <i class="fa-solid fa-pen" aria-hidden="true"></i> Modifica
            categoria
          </button>
        </p>

        <div class="crea-articolo-campi">
          <FormArticolo
            :key="apertura"
            ref="formArticolo"
            :category="categoriaUsata"
            :nome-iniziale="nomeIniziale"
            @update:valido="valido = $event"
          />
        </div>

        <p v-if="errore" class="help-error crea-articolo-errore">
          {{ errore }}
        </p>

        <div class="modal-actions">
          <button type="button" class="secondary-btn" @click="chiudi">
            Annulla
          </button>
          <button
            type="submit"
            class="primary-btn"
            :disabled="!valido || salvando"
            :title="
              valido
                ? 'Salva in catalogo e magazzino'
                : 'Compila i campi obbligatori (*) e correggi quelli in rosso'
            "
          >
            {{ salvando ? "Salvo…" : "Salva" }}
          </button>
        </div>
      </form>
    </template>
  </VueFinalModal>
</template>

<script>
import { VueFinalModal } from "vue-final-modal";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import FormArticolo from "@/components/FormArticolo.vue";
import { CATEGORIE } from "@/utilities/categorieArticolo";

export default {
  name: "CreaArticoloModal",
  components: { VueFinalModal, FormArticolo },
  props: {
    show: { type: Boolean, default: false },
    // testo già scritto nella ricerca articolo: diventa il nome
    nomeIniziale: { type: String, default: "" },
  },
  emits: ["update:show", "creato"],
  data() {
    return {
      categorie: CATEGORIE,
      categoria: "",
      // l'ultima scelta: tiene montato il form quando si torna al passo 1
      categoriaUsata: "",
      apertura: 0,
      valido: false,
      salvando: false,
      errore: "",
    };
  },
  computed: {
    etichettaCategoria() {
      return (
        this.categorie.find((c) => c.slug === this.categoria)?.label ||
        this.categoria
      );
    },
  },
  watch: {
    // ogni apertura riparte da capo, col testo della ricerca come nome
    show(aperta) {
      if (!aperta) return;
      this.categoria = "";
      this.categoriaUsata = "";
      this.apertura += 1;
      this.valido = false;
      this.errore = "";
    },
  },
  methods: {
    ...mapActions(["creaArticolo"]),
    scegliCategoria(slug) {
      this.categoria = slug;
      this.categoriaUsata = slug;
    },
    chiudi() {
      this.$emit("update:show", false);
    },
    async salva() {
      if (!this.valido || this.salvando) return;
      this.salvando = true;
      this.errore = "";
      try {
        const { data } = await this.creaArticolo(
          this.$refs.formArticolo.payload()
        );
        this.$toast.success("Articolo creato in catalogo e magazzino.");
        this.$emit("creato", data.item);
        this.chiudi();
      } catch (err) {
        this.errore =
          err.response?.data?.code === "ITEM_CODE_DUPLICATE"
            ? "Questo codice è già utilizzato da un altro articolo."
            : err.response?.data?.message ||
              err.response?.data?.error ||
              "Errore durante la creazione.";
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style>
.documents-modal .crea-articolo-modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 820px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  box-sizing: border-box;
  background: var(--modal-bg);
  color: var(--modal-text);
  border: 1px solid var(--modal-border);
  border-radius: 12px;
  box-shadow: var(--modal-shadow);
  padding: 1.5rem;
}

.crea-articolo-modal-content .crea-articolo-passo {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.crea-articolo-modal-content .crea-articolo-sottotitolo {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.crea-articolo-modal-content .crea-articolo-categorie {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
}

.crea-articolo-modal-content .crea-articolo-categoria {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--modal-border);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

/* colori della piattaforma: bordo nero al passaggio, rosso col focus */
.crea-articolo-modal-content .crea-articolo-categoria:hover {
  background: #f4f4f5;
  border-color: var(--text);
}

.crea-articolo-modal-content .crea-articolo-categoria:focus-visible,
.crea-articolo-modal-content .crea-articolo-cambia:focus-visible {
  outline: none;
  border-color: var(--brand);
  box-shadow: var(--ring);
}

.crea-articolo-modal-content .crea-articolo-cambia {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
  padding: 5px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.crea-articolo-modal-content .crea-articolo-cambia:hover {
  background: #f4f4f5;
  border-color: var(--text);
}

.crea-articolo-modal-content .crea-articolo-categoria i {
  width: 18px;
  text-align: center;
}

/* i campi scorrono, titolo e pulsanti restano */
.crea-articolo-modal-content .crea-articolo-campi {
  overflow-y: auto;
  max-height: calc(100vh - 260px);
}

/* nella finestra due colonne al posto delle tre della pagina */
.crea-articolo-modal-content .create-item-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crea-articolo-modal-content .field input,
.crea-articolo-modal-content .field textarea {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.crea-articolo-modal-content .crea-articolo-errore {
  margin: 0;
  color: #e6492d;
  font-size: 13px;
}

@media (max-width: 719px) {
  .documents-modal .crea-articolo-modal-content {
    padding: 1rem;
  }

  .crea-articolo-modal-content .create-item-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
